// Step 2 — Submit materials (Figma 8-13).
//
// Photo is required to continue (no-photo gate interstitial), then a resume or a
// note to casting, either one (decided 2026-10-08: "Show casting who you are"),
// and the 18+/guardian attestation. "Add materials later" (and the no-photo
// sheet's "Email me, I'll finish this later") saves what the page has, emails
// the link back and goes to the dashboard (docs/31); the step stays open, so
// the win-back emails follow up.
//
// Uploads go to the PM API; "Your photos" and the resume sheet offer what the
// actor already has there, and a returning actor's main headshot is preselected. At a
// free plan's photo limit the actor picks one of theirs or goes Pro; nothing in
// their library is ever replaced or deleted from here (decided 2026-10-06).

import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import i18next from "i18next";
import { iRootState } from "../../../store";
import { alreadySubmittedPath, rolePickerPath } from "../../../models/submissionFlowModel";
import { onPlan } from "../../../models/submissionStep4Model";
import SubmissionLayout from "../common/SubmissionLayout";
import PhotoCropSheet from "../common/PhotoCropSheet";
import {
  autoFrameFor,
  croppedFileName,
  exportCrop,
  ICropRect,
  isSupportedPhoto,
  loadImage,
  PHOTO_ACCEPT,
} from "../common/imageCrop";
import SubmissionSheet from "../common/SubmissionSheet";
import {
  getSubmissionFlowApi,
  IBreakdownCityDto,
  isMockApi,
} from "../../../shared/api/submission";
import { evaluateGeoMismatch, homeCityClearsGate } from "../common/geoMismatch";
import { isSubmissionAuthed } from "../../../helpers/submissionAuth";
import { fallbackTo, hasFlowContext } from "../common/flowGuard";
import { setSubmissionAnalyticsContext, trackSubmissionEvent } from "../analytics";
import { goToDashboard } from "../common/dashboard";
import { PhotoLibrary } from "../common/MediaLibraryPicker";
import { pickedGlow, ResumeChoices, ResumeSummary, UploadIcon } from "../common/ResumePicker";
import SignedInAs from "../common/SignedInAs";
import { keptCityOptions, loadCityOptions as loadSharedCityOptions } from "../common/cityOptions";
import { alphabetical, intlLocale } from "../common/alphabetical";
import {
  castingCallsPathFor,
  COUNTRY_NOT_ELIGIBLE,
  leaveForCastingCalls,
  leaveForDashboard,
  trackCountryClosedShown,
} from "../common/countryClosed";
import {
  EMPTY_LIBRARY,
  IMediaLibrary,
  preselectedPhoto,
  preselectedResume,
  proUpgradeUrl,
  withAsset,
} from "../common/mediaLibrary";
import {
  IAccountDefaultsDto,
  IMediaAssetDto,
  ISaveForLaterResponse,
  ISubmissionHomeDto,
} from "../../../shared/api/submission/types";
import {
  membershipPrimary,
  membershipPrimaryNotReady,
  membershipSecondary,
} from "../common/membershipButton";

const RESUME_ACCEPT = "application/pdf";
const NOTE_MAX = 1000;

interface Props extends StateProps, DispatchProps {
  history: any;
  location: any;
}

const SubmitMaterialsPage: React.FC<Props> = ({
  history,
  location,
  breakdownId,
  roleId,
  geoGuess,
  submissionInfo,
  draft,
  accountDefaults,
  photo,
  resume,
  note,
  ageAttested,
  loading,
  errorCode,
  errorMessage,
  membership,
  loadMembership,
  ensureDraft,
  uploadFile,
  saveMaterials,
  saveForLater,
  setNote,
  setAgeAttested,
  refreshGate,
  clearError,
  clearPhoto,
  pickPhoto,
  pickResume,
}) => {
  const [showNoPhotoGate, setShowNoPhotoGate] = useState(false);
  const [showResumeOrNoteGate, setShowResumeOrNoteGate] = useState(false);
  // Which footer button started the save in progress (its label says "One moment…").
  const [pressed, setPressed] = useState<"continue" | "later" | null>(null);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [showGeoInterstitial, setShowGeoInterstitial] = useState(false);
  // Home-city correction, revealed by "That's not where I live".
  const [showCityPicker, setShowCityPicker] = useState(false);
  // Filled at once when the code screen's preload has answered (cityOptions.ts).
  const [cityOptions, setCityOptions] = useState<IBreakdownCityDto[]>(() => keptCityOptions());
  const [chosenCountry, setChosenCountry] = useState("");
  const [chosenCityId, setChosenCityId] = useState("");
  const [savingCity, setSavingCity] = useState(false);
  const [cityError, setCityError] = useState<string | null>(null);
  const [homeCityName, setHomeCityName] = useState("");
  // The film center (the account's home city) at the bottom of the page: a row
  // with Change, or a picker when signup could not place the actor. Saving it
  // moves the plan to that country's (the API's home-location change).
  // Signed up in this tab (step 1's code made the account, so accountDefaults is in the store):
  // the store already answers what step 2 would wait for, so nothing waits (decided 2026-10-29).
  // A brand-new account has nothing in PM to pick, is a new signup, and its film center is the
  // city it was made with. A reload empties the store, and step 2 asks as usual.
  const justSignedUp = !!accountDefaults;
  const [home, setHome] = useState<ISubmissionHomeDto | null>(() =>
    accountDefaults ? homeFromDefaults(accountDefaults) : null
  );
  // The film center always shows (decided 2026-10-26): the draft carries it,
  // and when the draft didn't bring one (it failed, or the account's city
  // couldn't be placed) it's asked for on its own. If even that fails, the
  // card asks for it, so the field is never missing. Settled once we know.
  const [homeSettled, setHomeSettled] = useState(justSignedUp);
  const [showHomeSheet, setShowHomeSheet] = useState(false);
  // Continue pressed while the film center is still to choose (mandatory for a new signup
  // that signup couldn't place, decided 2026-10-29).
  const [homeError, setHomeError] = useState(false);
  // The events' account and market (analytics.ts): the server says new or existing with the home.
  useEffect(() => {
    if (home) setSubmissionAnalyticsContext({ newSignup: home.newSignup !== false, market: home.countryCode });
  }, [home]);
  const homeCountrySelect = useRef<HTMLSelectElement | null>(null);
  const [localFileError, setLocalFileError] = useState<string | null>(null);
  const [pendingPick, setPendingPick] = useState<"photo" | "resume" | null>(null);
  // The photo the visitor chose, kept after upload so Edit has something to
  // reopen. Held as an object URL rather than a data URL: a phone photo is
  // several megabytes, and base64 in React state costs a third more again for
  // no benefit.
  const [original, setOriginal] = useState<{
    url: string;
    name: string;
    crop: ICropRect | null;
  } | null>(null);
  // Only true while the visitor is adjusting the frame by hand.
  const [editorOpen, setEditorOpen] = useState(false);
  // What the dropzone shows. Comes from the CROPPED file, so the visitor sees
  // exactly what was uploaded rather than the original they picked.
  const [preview, setPreview] = useState<string | null>(null);
  const [framing, setFraming] = useState(false);
  // Set when Continue is pressed without the attestation, cleared when it is
  // ticked. The button used to do nothing at all in that case.
  const [ageError, setAgeError] = useState(false);
  const ageBox = useRef<HTMLInputElement | null>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const resumeInput = useRef<HTMLInputElement>(null);
  // What the actor already has in the PM API. Loaded once; uploads join it as they happen.
  const [library, setLibrary] = useState<IMediaLibrary>(EMPTY_LIBRARY);
  const [libraryLoaded, setLibraryLoaded] = useState(false);
  const preselectDone = useRef(false);
  // Step 2 opens behind a skeleton until the draft and the library are both back and the
  // preselection is made, so an existing actor's photo and resume don't pop in by themselves
  // (decided 2026-10-29).
  const [draftSettled, setDraftSettled] = useState(false);
  const [preselectSettled, setPreselectSettled] = useState(false);
  // What step 2 picked by itself: the line under the title says so, and a ring fades once.
  const [autoPicked, setAutoPicked] = useState({ photo: false, resume: false });
  // The resume sheet (option A, decided 2026-10-29), on the one it opened with.
  const [showResumePicker, setShowResumePicker] = useState(false);
  const [resumeChoice, setResumeChoice] = useState<number | null>(null);
  // The geo interstitial must not reappear after the user dismisses it —
  // refreshGate stores a new submissionInfo object, which re-runs the effect.
  const geoInterstitialSeen = useRef(false);

  useEffect(() => {
    // Every flow URL must carry `bd`. Without it there is nothing to submit
    // to, so hand the visitor to the traditional actor signup.
    if (!hasFlowContext({ search: location.search, breakdownId })) {
      history.replace(fallbackTo(location.search));
      return;
    }
    if (!isSubmissionAuthed()) {
      history.replace(`/submission/flow/verify-email${location.search}`);
      return;
    }
    trackSubmissionEvent("sf_materials_viewed", { breakdownId, roleId });
    clearError();
    ensureDraft({ breakdownId, roleId }).then((created: any) => {
      setDraftSettled(true);
      if (created && created.home) {
        setHomeSettled(true);
        return;
      }
      getSubmissionFlowApi()
        .getHome()
        .then((found) => {
          if (found) setHome(found);
        })
        .catch(() => undefined)
        .then(() => setHomeSettled(true));
    });
    refreshGate({ history, search: location.search });
    // Pro or a trial already: Continue skips step 3. Asked now, so Continue needn't wait.
    loadMembership();
    loadLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The library is a convenience: when it can't load, uploading still works.
  const loadLibrary = async () => {
    // An account made seconds ago has nothing in PM; an upload joins the library as it happens.
    if (justSignedUp) {
      setLibraryLoaded(true);
      return;
    }
    try {
      const api = getSubmissionFlowApi();
      const [photos, resumes] = await Promise.all([
        api.getExistingMedia("photo"),
        api.getExistingMedia("resume"),
      ]);
      setLibrary({ photos, resumes });
    } catch (e) {
      setLibrary(EMPTY_LIBRARY);
    } finally {
      setLibraryLoaded(true);
    }
  };

  // A returning actor starts with their main headshot, so Continue works at once — once the
  // draft is back, because a photo saved on it earlier comes first.
  useEffect(() => {
    if (preselectDone.current || !libraryLoaded || !draft) return;
    preselectDone.current = true;
    const main = preselectedPhoto(library, photo || draft.photo);
    if (main) {
      pickPhoto(main);
      trackSubmissionEvent("sf_photo_preselected", { photoNumber: main.assetId });
    }
    // Their main resume too, which completes "Show casting who you are" for most members.
    // Not on a draft that already has its own choice (a note alone is a choice).
    const mainResume = preselectedResume(library, resume || draft.resume, note || draft.noteToCastingDirector);
    if (mainResume) {
      pickResume(mainResume);
      trackSubmissionEvent("sf_resume_preselected", { resumeId: mainResume.assetId });
    }
    setAutoPicked({ photo: !!main, resume: !!mainResume });
    setPreselectSettled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryLoaded, draft]);

  // An upload joins the library at once, so a photo set aside stays one tap away.
  useEffect(() => {
    if (photo && photo.url) setLibrary((l) => ({ ...l, photos: withAsset(l.photos, photo) }));
  }, [photo]);
  useEffect(() => {
    if (resume) setLibrary((l) => ({ ...l, resumes: withAsset(l.resumes, resume) }));
  }, [resume]);

  // Country gate. NOT a city gate: submitting to another city in your own
  // country is normal and must never be blocked (see common/geoMismatch.ts).
  // It reads the account's home, as the server does (a film center picked in
  // another country gates too), and the IP's guess only when there is none. Not
  // before the home has answered: on the IP alone, a traveller whose home is
  // accepted would see the sheet open, then vanish.
  const homeCountry = home && home.located && home.countryCode ? home.countryCode.toUpperCase() : "";
  const gateGeo = homeCountry ? { countryCode: homeCountry, cityName: home!.cityName } : geoGuess;
  const mismatch = homeSettled ? evaluateGeoMismatch(gateGeo, submissionInfo) : null;
  // The actor's own city, for the note under the exits: saved on this page, or
  // the account's.
  const homeLabel = homeCityName || (homeCountry && home ? home.cityName : "");
  // The gate blocks rather than merely warning: a submission casting cannot see
  // is worse than a clear stop. Continue (and Add materials later: a draft for
  // a role they can't send to) give way to the two exits, the casting calls in
  // their country or the dashboard (countryClosed.ts). The server's refusal
  // stops it too, whatever the page knew.
  const countryClosed = !!mismatch || errorCode === COUNTRY_NOT_ELIGIBLE;
  const closedCountry = (mismatch && mismatch.userCountry) || homeCountry;
  useEffect(() => {
    if (countryClosed) trackCountryClosedShown("materials");
  }, [countryClosed]);

  useEffect(() => {
    if (geoInterstitialSeen.current) return;
    if (!mismatch) return;
    geoInterstitialSeen.current = true;
    setShowGeoInterstitial(true);
    trackSubmissionEvent("sf_geo_mismatch_shown", {
      userCountry: mismatch.userCountry,
      breakdownCountry: mismatch.breakdownCountry,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mismatch && mismatch.userCountry, mismatch && mismatch.breakdownCountry]);
  // Nothing left to gate on (a home in an accepted country, however it got
  // there): the sheet closes, and stays closed rather than coming back later.
  const gated = !!mismatch;
  useEffect(() => {
    if (!gated) setShowGeoInterstitial(false);
  }, [gated]);

  // Opening the picker from a modal branch has to wait until that branch has
  // actually closed — the hidden inputs live in the main view, so clicking the
  // ref during the same handler would hit a null (unmounted) node.
  useEffect(() => {
    if (!pendingPick) return;
    const input = pendingPick === "photo" ? photoInput.current : resumeInput.current;
    if (input) input.click();
    setPendingPick(null);
  }, [pendingPick]);

  // Every object URL this page creates is released, or a handful of them pin
  // whole multi-megabyte photos in memory for as long as the tab lives.
  //
  // Tracked in a ref, not read from state at unmount: an unmount cleanup with an
  // empty dependency list closes over the FIRST render's state, so it would have
  // revoked the nulls that were there before any photo was picked. Revoking an
  // already-revoked URL is a no-op, so the belt-and-braces costs nothing.
  const liveUrls = useRef<string[]>([]);
  const trackUrl = (url: string) => {
    liveUrls.current.push(url);
    return url;
  };
  const revoke = (url: string | null) => {
    if (url && URL.revokeObjectURL) URL.revokeObjectURL(url);
  };
  useEffect(
    () => () => {
      liveUrls.current.forEach(revoke);
      liveUrls.current = [];
    },
    []
  );

  // Uploads in progress. The store's loading flag can't say: the draft answering
  // during an upload clears it, and Continue then opened the no-photo gate over a
  // photo still on its way (seen with a 13 s upload, 2026-10-06).
  const [uploads, setUploads] = useState(0);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );
  const upload = async (payload: { kind: "photo" | "resume"; file: File }) => {
    setUploads((n) => n + 1);
    try {
      return await uploadFile(payload);
    } finally {
      if (mounted.current) setUploads((n) => n - 1);
    }
  };

  // A 0-byte file (iCloud/Drive placeholder, truncated download) would sail
  // through the photo gate and save an empty image, so reject it here.
  const acceptFile = (kind: "photo" | "resume", file: File | null): boolean => {
    if (!file) return false;
    if (file.size === 0) {
      setLocalFileError(i18next.t("That file looks empty. Please pick another one."));
      return false;
    }
    if (kind === "photo") {
      // Checked before anything is uploaded: framing has to decode the file
      // into a canvas, and a format the browser cannot decode would otherwise
      // reach the frame as a blank rectangle.
      if (!isSupportedPhoto(file)) {
        setLocalFileError(
          i18next.t("That file type isn't supported. Please use a JPG, PNG, GIF or BMP.")
        );
        return false;
      }
      setLocalFileError(null);
      autoFramePhoto(file);
      return true;
    }
    setLocalFileError(null);
    upload({ kind, file });
    return true;
  };

  const pickFile = (kind: "photo" | "resume") => (e: any) => {
    const file = e.target.files && e.target.files[0];
    acceptFile(kind, file);
    // Allow re-picking the same file after a failed upload.
    e.target.value = "";
  };

  const onDrop = (e: any) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    acceptFile("photo", file);
  };

  // Dropping a photo does not open the editor. The frame is chosen for the
  // visitor — see autoFrameFor — and uploaded straight away, because the
  // overwhelmingly common case is that the automatic frame is the right one and
  // an editor in the way of it is a chore. Adjusting it is one tap away.
  const autoFramePhoto = async (file: File) => {
    const url = trackUrl(URL.createObjectURL(file));
    setFraming(true);
    try {
      const image = await loadImage(url);
      const crop = autoFrameFor(image);
      const cropped = await exportCrop(image, crop, croppedFileName(file.name));

      const previousOriginal = original;
      const previousPreview = preview;
      // The original is kept, not revoked: Edit reopens it, and re-cropping a
      // crop would compound the quality loss every time.
      setOriginal({ url, name: file.name, crop });
      setPreview(trackUrl(URL.createObjectURL(cropped)));
      revoke(previousOriginal && previousOriginal.url);
      revoke(previousPreview);
      setFraming(false);

      trackSubmissionEvent("sf_photo_auto_framed", {
        sizeBytes: cropped.size,
        sourceWidth: image.naturalWidth,
        sourceHeight: image.naturalHeight,
      });
      uploadCropped(cropped);
    } catch (e) {
      setFraming(false);
      revoke(url);
      setLocalFileError(
        i18next.t("We couldn't open that image. Please try a JPG, PNG, GIF or BMP file.")
      );
    }
  };

  // Back to the empty dropzone. Nothing is deleted: the photo stays in the
  // actor's photos (their library is never changed from here), so there is
  // nothing to confirm, and "Your photos" picks it again (decided 2026-10-29).
  // Everything the photo touched on this page has to go: the
  // local preview, the original kept for Edit, the uploaded asset in the store
  // (Continue is gated on it), any error left over from the last attempt, and
  // the file input's value — without that last one, picking the SAME file again
  // fires no change event and the box would stay empty.
  const discardPhoto = () => {
    const previousOriginal = original;
    const previousPreview = preview;
    setEditorOpen(false);
    setOriginal(null);
    setPreview(null);
    setLocalFileError(null);
    revoke(previousOriginal && previousOriginal.url);
    revoke(previousPreview);
    clearPhoto();
    if (photoInput.current) photoInput.current.value = "";
    trackSubmissionEvent("sf_photo_discarded", {});
  };

  // Choosing one of the actor's own photos: nothing is uploaded, and there is no original to
  // re-frame (the PM API already cropped it).
  const pickLibraryPhoto = (asset: IMediaAssetDto) => {
    const previousOriginal = original;
    const previousPreview = preview;
    setOriginal(null);
    setPreview(null);
    setLocalFileError(null);
    revoke(previousOriginal && previousOriginal.url);
    revoke(previousPreview);
    clearError();
    pickPhoto(asset);
    trackSubmissionEvent("sf_photo_picked", { photoNumber: asset.assetId });
  };

  const pickLibraryResume = (asset: IMediaAssetDto) => {
    clearError();
    pickResume(asset);
    trackSubmissionEvent("sf_resume_picked", { resumeId: asset.assetId });
  };

  // Change, or "Add resume" for an actor who has resumes: the sheet, on the one picked. Without
  // any, "Add resume" uploads, as it always has.
  const openResumePicker = () => {
    if (!library.resumes.length) {
      if (resumeInput.current) resumeInput.current.click();
      return;
    }
    setResumeChoice(resume ? resume.assetId : null);
    setShowResumePicker(true);
    trackSubmissionEvent("sf_resume_picker_opened", { resumes: library.resumes.length });
  };

  const useChosenResume = () => {
    const chosen = library.resumes.filter((r) => r.assetId === resumeChoice)[0];
    setShowResumePicker(false);
    if (chosen && (!resume || chosen.assetId !== resume.assetId)) pickLibraryResume(chosen);
  };

  const onCroppedPhoto = (file: File, previewUrl: string, crop: ICropRect) => {
    const previousPreview = preview;
    setPreview(trackUrl(previewUrl));
    revoke(previousPreview);
    // Remember where they left the frame, so reopening Edit does not undo it.
    if (original) setOriginal({ ...original, crop });
    setEditorOpen(false);
    trackSubmissionEvent("sf_photo_cropped", { sizeBytes: file.size });
    uploadCropped(file);
  };

  // The box shows the cropped photo while it uploads. If the upload is refused (too large, or
  // a free plan's photos are full), the box goes back to the photo the submission still has,
  // so it never shows a picture that wasn't saved.
  const uploadCropped = async (file: File) => {
    const uploaded = await upload({ kind: "photo", file });
    if (uploaded) return;
    setPreview((current) => {
      revoke(current);
      return null;
    });
    setOriginal((current) => {
      revoke(current && current.url);
      return null;
    });
  };

  // The picker's options, shared for the visit and usually asked already (cityOptions.ts); they
  // come from the seam, so the mock serves them too.
  const loadCityOptions = async () => {
    if (cityOptions.length) return;
    // A retry shows "Loading countries…" again, not the last failure.
    setCityError(null);
    try {
      const options = await loadSharedCityOptions();
      // No cities is a failure too: kept, it would leave the picker loading for good.
      if (!options.length) throw new Error("No cities");
      setCityOptions(options);
    } catch (err) {
      setCityError(i18next.t("Could not load cities. Please try again."));
    }
  };

  const openCityPicker = async () => {
    setShowCityPicker(true);
    trackSubmissionEvent("sf_geo_fix_home_city_opened", {});
    await loadCityOptions();
  };

  // Change on the film center row: the sheet opens on the current one.
  const openHomeSheet = () => {
    setChosenCountry(home ? (home.countryCode || "").toUpperCase() : "");
    setChosenCityId(home ? String(home.cityId) : "");
    setCityError(null);
    setShowHomeSheet(true);
    trackSubmissionEvent("sf_film_center_change_opened", {});
    loadCityOptions();
  };

  // The draft says where the account lives; a save here updates it in place.
  useEffect(() => {
    if (draft && draft.home) setHome(draft.home);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft && draft.home && draft.home.cityId, draft && draft.home && draft.home.located]);

  // An existing user (decided 2026-10-28: older than 24 hours, or made by
  // another signup) is neither shown nor asked the film center or the age
  // statement. The server says which (home.newSignup) and accepts the
  // materials without the statement. Until it answers, neither shows.
  const existingUser = !!home && home.newSignup === false;
  // "Fix my home city" (the gate sheet and the note under the exits): new
  // signups only, and only once the home has answered, so an existing user
  // never gets it while we don't yet know they are one.
  const canFixHome = homeSettled && !existingUser;
  const askAge = homeSettled && !existingUser;
  const ageOk = ageAttested || existingUser;

  // Not placed (or not known at all): the picker is on the page, so its options
  // load with it, and Continue waits for a country and a city. New signups only:
  // an existing user is never asked (the server holds only a new signup's materials).
  const askHome = homeSettled && !existingUser && !(home && home.located);
  // Any new signup sees the film center, on the page (askHome) or behind Change: its options
  // load now, not on the tap (on a slow connection the sheet opened with an empty country list).
  useEffect(() => {
    if (homeSettled && !existingUser) loadCityOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeSettled, existingUser]);

  // A role that isn't the breakdown's can't be sent, at any step: the visitor
  // picks one of the breakdown's own roles. A breakdown that closed while the
  // page was open: the gate takes them to the closed page. One role cast while
  // others are open never lands here, the server lets it through.
  useEffect(() => {
    if (errorCode === "roleNotFound" && breakdownId) {
      history.replace(rolePickerPath(location.search, breakdownId, roleId));
    } else if (errorCode === "alreadySubmitted" && breakdownId) {
      history.replace(alreadySubmittedPath(location.search, breakdownId));
    } else if (errorCode === "breakdownClosed") {
      refreshGate({ history, search: location.search });
    } else if (errorCode === "homeLocationRequired") {
      // The server knows they still have to choose: the card asks, with its message.
      setHome((current) => (current ? { ...current, located: false } : current));
      setHomeError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCode]);

  const onSaveHomeCity = async () => {
    // Both, the country and a city in it: the country's first city would be a guess.
    const cityId = parseInt(chosenCityId, 10) || undefined;
    if (!chosenCountry || !cityId) return;
    setSavingCity(true);
    setCityError(null);
    try {
      const saved = await getSubmissionFlowApi().setHomeLocation({
        countryCode: chosenCountry,
        cityId,
      });
      setHomeCityName(saved.cityName || saved.countryCode);
      // A home they just chose doesn't need "Looks like you're in…": if casting doesn't
      // take its country, the page's own exits say so (countryClosed below).
      geoInterstitialSeen.current = true;
      // The account's flags stay: without newSignup an existing user would get the age box.
      setHome((current) => ({
        ...(current || {}),
        cityId: saved.cityId,
        cityName: saved.cityName || saved.countryCode,
        countryCode: saved.countryCode,
        countryName: undefined,
        located: true,
      }));
      setHomeError(false);
      trackSubmissionEvent("sf_geo_home_city_saved", {
        cityId: saved.cityId,
        countryCode: saved.countryCode,
        planRegionChanged: saved.planRegionChanged,
        from: showHomeSheet ? "filmCenterSheet" : showGeoInterstitial ? "countryGate" : "filmCenterCard",
      });
      if (!submissionInfo || homeCityClearsGate(saved, submissionInfo)) {
        // Same country as the breakdown — the gate is satisfied and the
        // submission continues. City differences never block. The home is
        // what the gate reads, so it clears by itself; a refusal from before
        // the fix goes too.
        setShowGeoInterstitial(false);
        setShowCityPicker(false);
        setShowHomeSheet(false);
        if (errorCode === COUNTRY_NOT_ELIGIBLE) clearError();
      } else if (showHomeSheet) {
        // Their film center, in a country casting doesn't take: the page's
        // exits replace Continue, and the note under them says why.
        setShowHomeSheet(false);
      } else {
        // Still another country: keep the sheet, but now it names the city the
        // user told us rather than the one their IP suggested.
        setCityError(
          i18next.t(
            "{{city}} is outside the country casting is accepting for this role.",
            { city: saved.cityName || saved.countryCode }
          )
        );
      }
    } catch (err) {
      const code = err && (err as any).errorCode;
      setCityError(
        code === "invalidCity" || code === "invalidCountry"
          ? i18next.t("Please choose a country and city from the list.")
          : code === "homeLocationLocked"
          ? i18next.t("Your plan is set for your country. Change your country from your account.")
          : i18next.t("Could not save your home city. Please try again.")
      );
    } finally {
      setSavingCity(false);
    }
  };

  const onBrowseNearMe = () => {
    trackSubmissionEvent("sf_geo_browse_near_me", {
      userCountry: mismatch ? mismatch.userCountry : "",
    });
    // The visitor's own market, the country the gate read (their home, or
    // their IP's), and never the breakdown's: that is the one they cannot
    // submit to. The API ranks it by city or province from the IP.
    history.push(castingCallsPathFor(closedCountry));
  };

  const onContinue = async () => {
    if (!photo) {
      setShowNoPhotoGate(true);
      trackSubmissionEvent("sf_no_photo_gate_shown", {});
      return;
    }
    if (!hasResumeOrNote) {
      setShowResumeOrNoteGate(true);
      trackSubmissionEvent("sf_resume_or_note_gate_shown", {});
      return;
    }
    if (askHome) {
      setHomeError(true);
      trackSubmissionEvent("sf_film_center_required_shown", {});
      if (homeCountrySelect.current) homeCountrySelect.current.focus();
      return;
    }
    if (!ageOk) {
      setAgeError(true);
      if (ageBox.current) ageBox.current.focus();
      return;
    }
    setPressed("continue");
    try {
      // The draft is created on mount; if that call failed, retry it here rather
      // than letting saveMaterials no-op silently on a null draft.
      if (!draft) {
        const created = await ensureDraft({ breakdownId, roleId });
        if (!created) return;
      }
      const saved = await saveMaterials();
      if (saved) {
        trackSubmissionEvent("sf_materials_saved", { submissionId: saved.submissionId });
        // Already on Pro or a trial: no phone to verify (decided 2026-10-27).
        const plan = membership || (await loadMembership());
        history.push(`/submission/flow/step/${onPlan(plan) ? 4 : 3}${location.search}`);
      }
    } finally {
      // Not on a page that has already moved on.
      if (mounted.current) setPressed(null);
    }
  };

  // Saved as it is, the link emailed, then the dashboard. A failed save keeps
  // the actor here with the error, everything still on the page; a failed
  // email doesn't hold them (the draft is saved, the win-backs follow up).
  const onAddLater = async (source: "page" | "noPhotoSheet") => {
    trackSubmissionEvent("sf_materials_deferred", { source });
    setShowNoPhotoGate(false);
    setPressed("later");
    try {
      if (!draft) {
        const created = await ensureDraft({ breakdownId, roleId });
        if (!created) return;
      }
      const saved = await saveForLater();
      if (!saved) return;
      trackSubmissionEvent("sf_materials_deferred_saved", {
        submissionId: saved.draft.submissionId,
        emailStatus: saved.emailStatus,
      });
      goToDashboard(history);
    } finally {
      // Not on a page that has already moved on.
      if (mounted.current) setPressed(null);
    }
  };

  // Local preview first, the stored asset second: coming back to this step in a
  // later session has no object URL, but the API may know the file's URL.
  const photoSrc = preview || (photo && photo.url) || null;

  const busy = loading || uploads > 0;
  // The skeleton's wait (above). A draft that didn't come back has nothing to preselect.
  const materialsReady = justSignedUp || (libraryLoaded && draftSettled && (preselectSettled || !draft));
  // While saving, "One moment…" goes on the button that was pressed only; both
  // buttons still disable. An upload in progress is Continue's wait.
  const laterWaiting = busy && pressed === "later";
  // A resume, or a note that is more than blank (the server's rule, HasResumeOrNote).
  const hasResumeOrNote = !!resume || note.trim().length > 0;
  const canContinue = !!photo && hasResumeOrNote && !askHome && ageOk && !busy && !countryClosed;
  // Figma 8 draws Continue greyed until the step is complete, and Figma 10 shows
  // the no-photo gate for someone who presses it anyway. Both can only hold if
  // the button LOOKS not-ready but still answers: disabled outright, the gate
  // was unreachable and a press without the attestation did nothing. So it is
  // truly disabled only while saving (the country gate replaces it outright).
  // Deliberately no aria-disabled on the not-ready state: that tells a screen
  // reader the control does nothing, when pressing it is exactly how the
  // visitor finds out what is missing.
  const continueBlocked = busy || !materialsReady;

  // The interstitials sit OVER the materials page in both Figma sections —
  // a bottom sheet on mobile, a centred modal on desktop — so they render
  // alongside the page rather than replacing it.
  // Both lists alphabetical, countries by the name shown (alphabetical.ts).
  const countryCodes: string[] = [];
  cityOptions.forEach((city) => {
    const code = (city.countryCode || "").toUpperCase();
    if (code && countryCodes.indexOf(code) < 0) countryCodes.push(code);
  });
  const countryOptions = alphabetical(countryCodes, (code) => countryLabel(code));
  const citiesInChosenCountry = alphabetical(
    cityOptions.filter((city) => (city.countryCode || "").toUpperCase() === chosenCountry),
    (city) => city.cityName
  );

  // Country, then city, then Save: the country gate's correction and the film
  // center use the same picker (country first: it is what the plan follows).
  const renderHomePicker = (opts: {
    cityPrompt: string;
    saveLabel: string;
    labelled?: boolean;
    secondary?: boolean;
    // The one Continue points to when the film center is still to choose.
    required?: boolean;
  }) => {
    const SaveButton: any = opts.secondary ? SecondaryButton : PrimaryButton;
    // Still on its way: the picker says so instead of showing an empty list.
    const optionsLoading = !cityOptions.length && !cityError;
    const countrySelect = (
      <CitySelect
        value={chosenCountry}
        disabled={optionsLoading}
        aria-busy={optionsLoading || undefined}
        innerRef={
          opts.required
            ? (el: HTMLSelectElement) => {
                homeCountrySelect.current = el;
              }
            : undefined
        }
        aria-invalid={opts.required && homeError ? true : undefined}
        aria-describedby={opts.required && homeError ? "film-center-error" : undefined}
        onChange={(e: any) => {
          const { value } = e.target;
          setChosenCountry(value);
          // A city from the previous country would be rejected.
          setChosenCityId("");
          setCityError(null);
        }}
        aria-label={opts.labelled ? undefined : i18next.t("Choose my home country")}
      >
        <option value="">
          {optionsLoading ? i18next.t("Loading countries…") : i18next.t("Choose my home country")}
        </option>
        {countryOptions.map((code) => (
          <option key={code} value={code}>
            {countryLabel(code)}
          </option>
        ))}
      </CitySelect>
    );
    const citySelect = (
      <CitySelect
        value={chosenCityId}
        onChange={(e: any) => {
          const { value } = e.target;
          setChosenCityId(value);
          setCityError(null);
        }}
        disabled={!chosenCountry || optionsLoading}
        aria-label={opts.labelled ? undefined : opts.cityPrompt}
      >
        <option value="">{opts.cityPrompt}</option>
        {citiesInChosenCountry.map((city) => (
          <option key={city.cityId} value={String(city.cityId)}>
            {city.cityName}
          </option>
        ))}
      </CitySelect>
    );
    return (
      <>
        {opts.labelled ? (
          <>
            <FieldLabel>
              {i18next.t("Country")}
              {countrySelect}
            </FieldLabel>
            <FieldLabel>
              {i18next.t("Film center")}
              {citySelect}
            </FieldLabel>
          </>
        ) : (
          <>
            {countrySelect}
            {citySelect}
          </>
        )}
        {cityError && (
          <SheetError role="alert">
            {cityError}{" "}
            {!cityOptions.length && (
              <RetryButton type="button" onClick={() => loadCityOptions()}>
                {i18next.t("Try again")}
              </RetryButton>
            )}
          </SheetError>
        )}
        <SaveButton onClick={onSaveHomeCity} disabled={!chosenCountry || !chosenCityId || savingCity || optionsLoading}>
          {savingCity ? i18next.t("One moment…") : opts.saveLabel}
        </SaveButton>
      </>
    );
  };

  const sheets = (
    <>
      {editorOpen && original && (
        <PhotoCropSheet
          imageUrl={original.url}
          fileName={original.name}
          initialCrop={original.crop}
          onCancel={() => setEditorOpen(false)}
          onConfirm={onCroppedPhoto}
        />
      )}

      {showGeoInterstitial && mismatch && (
        <SubmissionSheet
          title={i18next.t("Looks like you're in {{city}}", {
            city: mismatch.userLabel,
          })}
          onDismiss={() => setShowGeoInterstitial(false)}
          dismissOnScrim={!showCityPicker}
        >
          <SheetText>
            {i18next.t(
              "Casting is only seeing submissions from {{city}} for this one. Here's what's open near you right now.",
              { city: mismatch.breakdownCityName }
            )}
          </SheetText>

          {/* Figma 8.1 shows the correction FIRST and the browse CTA last: the
              likeliest reason to be in the wrong country is a trip or a VPN,
              not a wish to browse. An existing user's country isn't changed
              here (decided 2026-11-02): no correction, only the exits. */}
          {!showCityPicker && canFixHome && (
            <TextButton onClick={openCityPicker}>
              {i18next.t("That's not where I live. Fix my home city")}
            </TextButton>
          )}

          {/* Country first: it is the field the gate reads, and changing it
              changes the plan region. City is optional — someone may know
              their country before their city, and not every country has a
              city list. */}
          {showCityPicker &&
            renderHomePicker({
              cityPrompt: i18next.t("Choose my home city"),
              saveLabel: i18next.t("Save my home city"),
            })}

          {/* Hierarchy follows the action on screen: with the picker open the
              visitor is correcting their home city, so Save is the primary and
              browsing drops to secondary. With the picker closed there is no
              competing action and this is the primary (Figma 8.1). */}
          {showCityPicker ? (
            <SecondaryButton onClick={onBrowseNearMe}>
              {i18next.t("Show me roles in {{city}}", { city: mismatch.userLabel })}
            </SecondaryButton>
          ) : (
            <PrimaryButton onClick={onBrowseNearMe}>
              {i18next.t("Show me roles in {{city}}", { city: mismatch.userLabel })}
            </PrimaryButton>
          )}
        </SubmissionSheet>
      )}

      {showHomeSheet && (
        <SubmissionSheet
          title={i18next.t("Your film center")}
          onDismiss={() => setShowHomeSheet(false)}
        >
          <SheetText>{i18next.t("Where you're based as an actor.")}</SheetText>
          {renderHomePicker({
            cityPrompt: i18next.t("Choose my film center"),
            saveLabel: i18next.t("Save my film center"),
            labelled: true,
          })}
          <TextButton onClick={() => setShowHomeSheet(false)}>{i18next.t("Cancel")}</TextButton>
        </SubmissionSheet>
      )}


      {showResumeOrNoteGate && (
        <SubmissionSheet
          title={i18next.t("Casting needs a resume or a few words")}
          onDismiss={() => setShowResumeOrNoteGate(false)}
        >
          <SheetText>
            {i18next.t(
              "Add your resume, or tell casting in a line or two why you're right for this role."
            )}
          </SheetText>
          {/* The resume leads; the note is the quick way out for anyone
              who has no resume yet. */}
          <PrimaryButton
            onClick={() => {
              setShowResumeOrNoteGate(false);
              setPendingPick("resume");
            }}
          >
            {i18next.t("Upload my resume")}
          </PrimaryButton>
          <SecondaryButton
            type="button"
            onClick={() => {
              setShowResumeOrNoteGate(false);
              setShowNoteEditor(true);
            }}
          >
            {i18next.t("Write a note")}
          </SecondaryButton>
        </SubmissionSheet>
      )}

      {showResumePicker && (
        <SubmissionSheet
          title={i18next.t("Choose a resume")}
          onDismiss={() => setShowResumePicker(false)}
        >
          <SheetText>
            {i18next.t("Casting sees the one you choose. Your resumes stay as they are.")}
          </SheetText>
          <ResumeChoices
            resumes={library.resumes}
            selectedId={resumeChoice}
            onSelect={(r) => setResumeChoice(r.assetId)}
          />
          <UploadNewButton
            type="button"
            onClick={() => {
              setShowResumePicker(false);
              setPendingPick("resume");
              trackSubmissionEvent("sf_resume_picker_upload", {});
            }}
          >
            <UploadIcon />
            <span>{i18next.t("Upload a new PDF")}</span>
          </UploadNewButton>
          <PrimaryButton onClick={useChosenResume} disabled={!resumeChoice}>
            {i18next.t("Use this resume")}
          </PrimaryButton>
          {resume && (
            <TextButton
              onClick={() => {
                setShowResumePicker(false);
                pickResume(null);
                trackSubmissionEvent("sf_resume_removed", {});
              }}
            >
              {i18next.t("Don't include a resume")}
            </TextButton>
          )}
        </SubmissionSheet>
      )}

      {showNoPhotoGate && (
        <SubmissionSheet
          title={i18next.t("Casting can't cast what they can't see")}
          onDismiss={() => setShowNoPhotoGate(false)}
        >
          <SheetText>
            {i18next.t(
              "Every submission needs a photo. It doesn't have to be a professional headshot. It has to look like you."
            )}
          </SheetText>
          <TextButton onClick={() => onAddLater("noPhotoSheet")}>
            {i18next.t("Email me, I'll finish this later")}
          </TextButton>
          <PrimaryButton
            onClick={() => {
              // Close the sheet first, then open the picker from the effect once
              // the page (which owns the hidden inputs) is interactive again.
              setShowNoPhotoGate(false);
              setPendingPick("photo");
            }}
          >
            {i18next.t("Add my photo")}
          </PrimaryButton>
        </SubmissionSheet>
      )}

      {showNoteEditor && (
        <NoteEditorSheet
          note={note}
          onSave={(text) => {
            setNote(text);
            setShowNoteEditor(false);
          }}
          onCancel={() => setShowNoteEditor(false)}
        />
      )}
    </>
  );

  return (
    <SubmissionLayout step={2}>
      {sheets}
      <CenterWrap>
        <Title>{i18next.t("Submit materials")}</Title>
        <SubTitle role="status">
          {autoPicked.photo && autoPicked.resume
            ? i18next.t("We picked your main headshot and newest resume. Change either one below.")
            : autoPicked.photo
            ? i18next.t("We picked your main headshot. Tap it to replace it.")
            : autoPicked.resume
            ? i18next.t("We picked your newest resume. Change it below.")
            : i18next.t("Add a photo that looks like you walking into the audition.")}
        </SubTitle>
      </CenterWrap>

      <input
        ref={photoInput}
        type="file"
        accept={PHOTO_ACCEPT}
        onChange={pickFile("photo")}
        style={{ display: "none" }}
      />
      <input
        ref={resumeInput}
        type="file"
        accept={RESUME_ACCEPT}
        onChange={pickFile("resume")}
        style={{ display: "none" }}
      />

      {materialsReady ? (
        <>
          <Dropzone
            hasPhoto={!!photo}
            picked={autoPicked.photo}
            dragging={dragging}
            onClick={() => photoInput.current && photoInput.current.click()}
            onDragOver={(e: any) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            role="button"
            tabIndex={0}
            aria-label={i18next.t("Upload a photo")}
            onKeyPress={(e: any) => {
              if (e.key === "Enter" || e.key === " ") {
                photoInput.current && photoInput.current.click();
              }
            }}
          >
            {framing ? (
              <FramingNote aria-busy="true">{i18next.t("Framing your photo…")}</FramingNote>
            ) : photoSrc ? (
              // The point of the whole step: the visitor sees their own face, at the
              // ratio casting will see it, before they commit to it. A filename in a
              // chip told them nothing about whether the crop was any good.
              <PhotoPreviewWrap>
                <PhotoPreview src={photoSrc} alt={i18next.t("Your headshot")} />
                <PreviewHint>{i18next.t("Tap to replace")}</PreviewHint>
              </PhotoPreviewWrap>
            ) : photo ? (
              <PhotoChip>
                <span aria-hidden="true">🖼</span>
                <ChipName>{photo.fileName}</ChipName>
                <ChipHint>{i18next.t("Tap to replace")}</ChipHint>
              </PhotoChip>
            ) : (
              <>
                <PlusCircle aria-hidden="true">+</PlusCircle>
                <DropText>{i18next.t("Drop here or click to upload")}</DropText>
              </>
            )}
          </Dropzone>

          {/* Two ways out of an automatic frame that got it wrong: adjust it, or
              take the photo off and start over. Both sit OUTSIDE the dropzone —
              the box itself is still tap-to-replace, and an action inside it would
              make a tap on the photo ambiguous.

              Centred label with the X at the far right. An X, not a trash can:
              it takes the photo off this submission and deletes nothing. */}
          {photoSrc && !framing && (
            <PhotoActions>
              {original && (
                <PhotoAction
                  type="button"
                  onClick={() => {
                    trackSubmissionEvent("sf_photo_editor_opened", {});
                    setEditorOpen(true);
                  }}
                >
                  {i18next.t("Edit photo frame")}
                </PhotoAction>
              )}
              <DiscardButton
                type="button"
                onClick={discardPhoto}
                title={i18next.t("Remove from this submission")}
                aria-label={i18next.t("Remove from this submission")}
              >
                <CloseIcon aria-hidden="true" />
              </DiscardButton>
            </PhotoActions>
          )}

          {/* Shown only when there is another photo to choose: a new account's one upload is
              already in the box above. */}
          {!framing && library.photos.some((p) => !photo || p.assetId !== photo.assetId) && (
            <PhotoLibrary
              photos={library.photos}
              selectedId={photo ? photo.assetId : undefined}
              onPick={pickLibraryPhoto}
            />
          )}
        </>
      ) : (
        <PhotoSkeleton aria-hidden="true" />
      )}

      {/* A resume or a note, either one: the two rows as one group, so the choice
          reads before Continue does. Once one is in, the other says it's optional. */}
      <ResumeOrNote>
        <GroupHead>
          <GroupTitle>
            {i18next.t("Show casting who you are")}
            {hasResumeOrNote && <GroupDone aria-hidden="true">✓</GroupDone>}
          </GroupTitle>
          <GroupHint>{i18next.t("A resume or a quick note — or both.")}</GroupHint>
        </GroupHead>

      {!materialsReady ? (
        <ResumeSkeleton aria-hidden="true">
          <SkeletonBlock style={{ width: 26, height: 32, flex: "none", marginRight: 12 }} />
          <SkeletonLines>
            <SkeletonBlock style={{ width: "70%", height: 12 }} />
            <SkeletonBlock style={{ width: "45%", height: 10, marginTop: 6 }} />
          </SkeletonLines>
        </ResumeSkeleton>
      ) : resume ? (
        <ResumeSummary resume={resume} onChange={openResumePicker} highlight={autoPicked.resume} />
      ) : (
        <RowButton onClick={openResumePicker} type="button">
          <RowLabel>
            {note.trim() ? i18next.t("Add resume (Optional)") : i18next.t("Add resume")}
          </RowLabel>
          <RowPlus aria-hidden="true">+</RowPlus>
        </RowButton>
      )}

      <RowButton onClick={() => setShowNoteEditor(true)} type="button">
        <RowLabel>
          {note
            ? `${note.slice(0, 34)}${note.length > 34 ? "…" : ""}`
            : resume
            ? i18next.t("Note to Casting Director (Optional)")
            : i18next.t("Note to Casting Director")}
        </RowLabel>
        <RowPlus aria-hidden="true">{note ? "✓" : "+"}</RowPlus>
      </RowButton>
      </ResumeOrNote>

      {home && home.located && !existingUser && (
        <FilmCenterRow>
          <PinIcon />
          <FilmCenterText>
            <FilmCenterLabel>{i18next.t("Film center:")}</FilmCenterLabel>{" "}
            <strong>
              {[home.cityName, countryLabel(home.countryCode || "", home.countryName)]
                .filter((part) => !!part)
                .join(", ")}
            </strong>
          </FilmCenterText>
          <FilmCenterChange type="button" onClick={openHomeSheet}>
            {i18next.t("Change")}
          </FilmCenterChange>
        </FilmCenterRow>
      )}
      {askHome && (
        <FilmCenterCard>
          <FilmCenterTitle>
            <PinIcon />
            {i18next.t("Your film center")}
          </FilmCenterTitle>
          <FilmCenterHint>
            {i18next.t("We couldn't tell where you are. Choose where you're based as an actor.")}
          </FilmCenterHint>
          {renderHomePicker({
            cityPrompt: i18next.t("Choose my film center"),
            saveLabel: i18next.t("Save my film center"),
            labelled: true,
            secondary: true,
            required: true,
          })}
          {homeError && (
            <FilmCenterError id="film-center-error" role="alert">
              {i18next.t("Choose your country and film center before you continue.")}
            </FilmCenterError>
          )}
        </FilmCenterCard>
      )}

      {askAge && (
        <AgeRow>
          <Checkbox
            type="checkbox"
            checked={ageAttested}
            onChange={(e: any) => {
              setAgeAttested(e.target.checked);
              if (e.target.checked) setAgeError(false);
            }}
            id="age-attest"
            aria-invalid={ageError}
            aria-describedby={ageError ? "age-attest-error" : undefined}
            innerRef={(el: HTMLInputElement) => {
              ageBox.current = el;
            }}
          />
          <AgeLabel htmlFor="age-attest">
            {i18next.t(
              "I am currently at least 18 years old or this is being filled by a parent or guardian"
            )}
          </AgeLabel>
        </AgeRow>
      )}
      {askAge && ageError && (
        <AgeError id="age-attest-error" role="alert">
          {i18next.t("Please confirm this before you continue.")}
        </AgeError>
      )}

      {localFileError && <ErrorText role="alert">{localFileError}</ErrorText>}

      {/* homeLocationRequired: the film center card says it, where the fix is. */}
      {/* countryNotEligible: said in the note under the exits instead. */}
      {errorCode && errorCode !== "homeLocationRequired" && errorCode !== COUNTRY_NOT_ELIGIBLE && (
        <ErrorText role="alert">
          {errorCode === "fileTooLarge"
            ? i18next.t("That file is too large.")
            : errorCode === "unsupportedFileType"
            ? i18next.t("That file type isn't supported.")
            : errorCode === "photoLimitReached"
            ? (
              <>
                {i18next.t(
                  "Your free plan's photos are full. Choose one of your photos, or go PRO for up to 16."
                )}{" "}
                <ProLink href={proUpgradeUrl()} target="_blank" rel="noopener noreferrer">
                  {i18next.t("Go PRO")}
                </ProLink>
              </>
            )
            : errorCode === "photoNotFound"
            ? i18next.t("We couldn't find that photo. Please upload it again.")
            : errorCode === "resumeNotFound"
            ? i18next.t("We couldn't find that resume. Please upload it again.")
            : errorCode === "resumeOrNoteRequired"
            ? i18next.t("Add a resume or a note to casting before you continue.")
            : errorCode === "noteTooLong"
            ? i18next.t("Your note is too long.")
            : errorCode === "breakdownClosed" || errorCode === "roleCast"
            ? i18next.t("This role is no longer open.")
            : errorCode === "network"
            ? errorMessage
            : i18next.t("Something went wrong. Please try again.")}
        </ErrorText>
      )}

      {countryClosed ? (
        <>
          {/* Casting doesn't take their country: no Continue, two ways on
              (countryClosed.ts), and the fix for a home that is wrong. */}
          <ExitButtons>
            <PrimaryButton onClick={() => leaveForCastingCalls(history, closedCountry, "materials")}>
              {closedCountry
                ? i18next.t("See casting calls in {{country}}", { country: countryLabel(closedCountry) })
                : i18next.t("See open casting calls")}
            </PrimaryButton>
            <SecondaryButton onClick={() => leaveForDashboard(history, "materials")}>
              {i18next.t("Go to my dashboard")}
            </SecondaryButton>
          </ExitButtons>
          <GateNote role="status">
            <div>{i18next.t("Casting for this role isn't open to your country.")}</div>
            {(homeLabel || mismatch) && (
              <div>
                {homeLabel
                  ? i18next.t("Your home city is set to {{city}}.", { city: homeLabel })
                  : i18next.t("Casting is only seeing submissions from {{city}} for this one.", {
                      city: mismatch!.breakdownCityName,
                    })}
              </div>
            )}
            {/* The sheet is dismissible, so this is the way back to it; with no
                mismatch on the page (the server's refusal), the film center.
                New signups only: an existing user's country stays as it is, so
                they can't submit here, only take one of the exits. */}
            {canFixHome && (
              <TextButton onClick={() => (mismatch ? setShowGeoInterstitial(true) : setShowHomeSheet(true))}>
                {i18next.t("That's not where I live. Fix my home city")}
              </TextButton>
            )}
          </GateNote>
        </>
      ) : (
        <>
          <TextButton onClick={() => onAddLater("page")} disabled={busy || !materialsReady}>
            {laterWaiting ? i18next.t("One moment…") : i18next.t("Add materials later")}
          </TextButton>

          <PrimaryButton
            onClick={onContinue}
            disabled={continueBlocked}
            notReady={!canContinue}
          >
            {(busy && !laterWaiting) || !materialsReady ? i18next.t("One moment…") : i18next.t("Continue")}
          </PrimaryButton>
        </>
      )}

      <SignedInAs history={history} search={location.search} step={2} />

      {isMockApi() && (
        <MockHint>
          {`mock: any image/pdf works · >10MB → 413 · draft ${
            draft ? draft.submissionId : "…"
          }`}
        </MockHint>
      )}
    </SubmissionLayout>
  );
};

const CenterWrap = styled.div`
  text-align: center;
  margin: 24px 0 20px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #122640;
  margin: 0 0 8px;
`;

const SubTitle = styled.p`
  font-size: 14px;
  color: #53627c;
  margin: 0 0 20px;
  line-height: 21px;
`;

// 4:5 from the first paint, matching the crop frame and the file that leaves
// the editor. The empty box is a promise about the shape casting will see, and
// it means the photo drops into a space already its own size instead of
// resizing the page the moment it arrives.
//
// min-height carries the ratio on its own at the 260px width this box is at
// on every viewport it renders in; aspect-ratio makes it exact, and keeps it
// exact if the max-width is ever changed. Content-box, so both apply to the
// area the photo fills rather than to the dashed border around it.
const Dropzone: any = styled.div`
  border-radius: 8px;
  background-color: #f2f4f7;
  max-width: 260px;
  min-height: 325px;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: 0 auto 14px;
  border: 2px dashed transparent;

  ${(p: any) =>
    p.dragging &&
    css`
      border-color: #0f9bf0;
      background-color: #eaf6fe;
    `}

  ${(p: any) =>
    p.hasPhoto &&
    css`
      background-color: #eaf6fe;
    `}

  ${(p: any) => p.picked && pickedGlow}
`;

// Step 2's wait (materialsReady): the shapes of what is coming, at their own size, so nothing
// moves when it arrives.
const shimmer = css`
  @keyframes sf-skeleton-shimmer {
    0% {
      background-position: -240px 0;
    }
    100% {
      background-position: 240px 0;
    }
  }
  background: #e9eef4 linear-gradient(90deg, #e9eef4 0, #f5f8fb 50%, #e9eef4 100%) no-repeat;
  background-size: 240px 100%;
  animation: sf-skeleton-shimmer 1.3s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const PhotoSkeleton = styled.div`
  ${shimmer}
  border-radius: 8px;
  max-width: 260px;
  min-height: 325px;
  aspect-ratio: 4 / 5;
  margin: 0 auto 14px;
`;

const ResumeSkeleton = styled.div`
  display: flex;
  align-items: center;
  background-color: #f7f9fb;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 10px;
  min-height: 48px;
  box-sizing: border-box;
`;

const SkeletonLines = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const SkeletonBlock = styled.div`
  ${shimmer}
  border-radius: 3px;
`;

// The sheet's way to a new file: dashed, as the empty photo box is, so it reads as "add".
const UploadNewButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: none;
  min-height: 48px;
  margin: 8px 0 4px;
  border: 1px dashed #8a94a6;
  border-radius: 8px;
  background: #ffffff;
  color: #0b7cc2;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  > span {
    margin-left: 8px;
  }
`;

const PlusCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #8a94a6;
  color: #8a94a6;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`;

const DropText = styled.div`
  font-size: 13px;
  color: #53627c;
  text-align: center;
  max-width: 140px;
`;

// Hand-drawn rather than an icon dependency or an emoji: 16px of path data,
// stroked in `currentColor` so the button owns the colour and the hover state.
const CloseIcon: React.FC<{ "aria-hidden"?: boolean | "true" }> = (props) => (
  <svg
    {...props}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    focusable="false"
  >
    <path d="M4 4l8 8M12 4l-8 8" />
  </svg>
);

const FramingNote = styled.div`
  font-size: 13px;
  color: #53627c;
`;

// The label is centred on the row and the discard button is pinned to the right
// edge, so the label stays optically centred under the photo however wide the
// column gets. Absolute rather than a three-column layout: there is no third
// item to balance, and a spacer for symmetry is a lie about the layout.
const PhotoActions = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  margin: -6px 0 14px;
`;

const PhotoAction = styled.button`
  background: none;
  border: none;
  color: #0f9bf0;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 6px;
  cursor: pointer;
  min-height: 40px;
`;

const DiscardButton = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  /* 40px of tap target around a 16px glyph: the icon is small, the thing you
     have to hit is not. */
  width: 40px;
  height: 40px;
  padding: 0;
  background: none;
  border: none;
  border-radius: 6px;
  /* Grey, not red: nothing is destroyed. */
  color: #53627c;
  cursor: pointer;

  &:hover {
    background: #eef2f7;
    color: #1d2b44;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(15, 155, 240, 0.28);
  }
`;

// The box IS the photo's shape now, so the photo fills it edge to edge — a
// small picture floating inside a large frame of its own proportions just looks
// unfinished.
const PhotoPreviewWrap = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const PhotoPreview = styled.img`
  width: 100%;
  height: 100%;
  /* Both are already 4:5, so object-fit cover crops nothing — it is here to guarantee no
     distortion if a photo ever arrives at another ratio. */
  object-fit: cover;
  display: block;
  background: #e3e8ee;
`;

// Overlaid rather than stacked underneath: there is no room below the photo any
// more, and a caption over the foot of an image is legible as long as it brings
// its own ground.
const PreviewHint = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 6px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
  text-align: center;
  background: rgba(18, 38, 64, 0.62);
`;

const PhotoChip = styled.div`
  text-align: center;
  font-size: 28px;
`;

const ChipName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #122640;
  margin-top: 8px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChipHint = styled.div`
  font-size: 11px;
  color: #53627c;
  margin-top: 4px;
`;

const ResumeOrNote = styled.div`
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  padding: 14px 14px 4px;
  margin-bottom: 16px;
`;

const GroupHead = styled.div`
  margin-bottom: 12px;
`;

const GroupTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 700;
  color: #0a1f44;
`;

const GroupDone = styled.span`
  margin-left: 8px;
  color: #0bb07b;
  font-size: 16px;
`;

const GroupHint = styled.div`
  font-size: 13px;
  color: #53627c;
  margin-top: 2px;
`;

const RowButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f7f9fb;
  border: none;
  border-radius: 6px;
  padding: 14px;
  margin-bottom: 10px;
  cursor: pointer;
  min-height: 48px;
  text-align: left;
`;

const ProLink = styled.a`
  color: #0f9bf0;
  font-weight: 600;
  white-space: nowrap;
`;

const RowLabel = styled.span`
  font-size: 14px;
  color: #53627c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowPlus = styled.span`
  color: #8a94a6;
  font-size: 18px;
  flex-shrink: 0;
  margin-left: 8px;
`;

const AgeRow = styled.div`
  display: flex;
  align-items: flex-start;
  margin: 16px 0;
`;

const Checkbox = styled.input`
  margin: 3px 10px 0 0;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
`;

const AgeError = styled.div`
  font-size: 13px;
  color: #c7053f;
  margin: -8px 0 12px 28px;
`;

const AgeLabel = styled.label`
  font-size: 13px;
  color: #53627c;
  line-height: 19px;
`;

// Body copy inside a sheet (the sheet supplies its own centred title).
const SheetText = styled.p`
  font-size: 14px;
  color: #53627c;
  line-height: 1.5;
  /* Spacing inside a sheet comes from the sheet's flex gap. */
  margin: 0 0 4px;
`;

// The note editor keeps its draft to itself: with the draft in the page, every
// keystroke re-rendered all of step 2 behind the sheet (15-20 ms a character, 60-80
// on a slow CPU), and typing and the Continue button felt laggy. The page hears
// about the note once, on Continue.
const NoteEditorSheet: React.FC<{
  note: string;
  onSave: (note: string) => void;
  onCancel: () => void;
}> = ({ note, onSave, onCancel }) => {
  const [draft, setDraft] = useState(note);
  return (
    <SubmissionSheet title={i18next.t("Why you?")} onDismiss={onCancel} dismissOnScrim={false}>
      <SheetText>
        {i18next.t("Casting reads these. A few honest lines beat a paragraph of adjectives.")}
      </SheetText>
      <NoteArea
        value={draft}
        maxLength={NOTE_MAX}
        onChange={(e: any) => setDraft(e.target.value)}
        onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
          // Enter continues, as the button does; Shift+Enter starts a new line.
          if (e.key !== "Enter" || e.shiftKey || (e.nativeEvent as any).isComposing) return;
          e.preventDefault();
          onSave(draft);
        }}
        placeholder={i18next.t("I've played this guy before, and I know exactly who he is…")}
        aria-label={i18next.t("Note to Casting Director")}
      />
      <CharCount>{`${draft.length}/${NOTE_MAX}`}</CharCount>
      <PrimaryButton onClick={() => onSave(draft)}>{i18next.t("Continue")}</PrimaryButton>
      <TextButton onClick={onCancel}>{i18next.t("Skip for now")}</TextButton>
    </SubmissionSheet>
  );
};

const NoteArea = styled.textarea`
  width: 100%;
  min-height: 160px;
  border: 1px solid #c9d3e0;
  border-radius: 6px;
  padding: 12px;
  font-size: 14px;
  line-height: 20px;
  outline: 0;
  resize: vertical;

  &:focus {
    border-color: #0f9bf0;
  }
`;

const CharCount = styled.div`
  text-align: right;
  font-size: 11px;
  color: #8a94a6;
  margin: 0;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #c7053f;
  text-align: center;
  margin-bottom: 12px;
`;

const FilmCenterError = styled.div`
  font-size: 13px;
  color: #c7053f;
  margin-top: 8px;
`;

const CitySelect: any = styled.select`
  width: 100%;
  height: 48px;
  min-height: 48px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #c9d3e0;
  border-radius: 5px;
  background: #ffffff;
  font-size: 15px;
  color: #122640;
`;

// The country's name in the page's language ("Mexico", "Mexique", "México");
// the code when the browser can't name it.
// The film center as signup made it (verify-email's accountDefaults), until the draft brings the
// server's: placed unless the city was only the breakdown's guess or nothing (the server's
// Unlocated rungs).
const homeFromDefaults = (defaults: IAccountDefaultsDto): ISubmissionHomeDto => ({
  cityId: defaults.cityId || 0,
  cityName: defaults.cityName || "",
  countryCode: defaults.countryCode,
  located: !!defaults.cityId && defaults.geoSource !== "breakdownFallback" && defaults.geoSource !== "none",
  newSignup: true,
});

const countryLabel = (code: string, fallback?: string): string => {
  if (!code) return fallback || "";
  try {
    const DisplayNames = (Intl as any).DisplayNames;
    if (DisplayNames) {
      const name = new DisplayNames([intlLocale(), "en"], { type: "region" }).of(code.toUpperCase());
      if (name && name !== code.toUpperCase()) return name;
    }
  } catch (e) {
    // an unknown code or an old browser: fall through
  }
  return fallback || code.toUpperCase();
};

const PinIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#4e5b72"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flex: "none" }}
  >
    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </svg>
);

const FilmCenterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  padding: 0 4px 0 12px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #122640;
`;

const FilmCenterText = styled.span`
  flex-grow: 1;
  min-width: 0;
`;

const FilmCenterLabel = styled.span`
  color: #53627c;
`;

const FilmCenterChange = styled.button`
  flex: none;
  border: 0;
  background: none;
  padding: 13px 10px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: #0f9bf0;
  cursor: pointer;
  &:hover {
    color: #0584d7;
  }
`;

const FilmCenterCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 16px;
`;

const FilmCenterTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: #122640;
`;

const FilmCenterHint = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: #53627c;
`;

const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #122640;
`;

const SheetError = styled.div`
  font-size: 13px;
  color: #c7053f;
  margin: 0;
`;

// "Try again" inside SheetError: a link-like button on the error's line.
const RetryButton = styled.button`
  padding: 2px;
  background: none;
  border: 0;
  font-size: 13px;
  font-weight: 600;
  color: #0f9bf0;
  text-decoration: underline;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #0584d7;
    outline-offset: 2px;
  }
`;

// The two exits when casting doesn't take the actor's country, apart like the sheet's buttons.
const ExitButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: none;
`;

const GateNote = styled.div`
  font-size: 13px;
  color: #53627c;
  text-align: center;
  margin: 10px 0 0;
`;

const PrimaryButton: any = styled.button`
  width: 100%;
  flex: none;
  ${membershipPrimary}
  ${(p: any) => p.notReady && membershipPrimaryNotReady}
  padding: 14px 0;
  min-height: 48px;
`;

// Outlined counterpart to PrimaryButton: same footprint, no fill, so a sheet
// can offer a second real action without two dark blocks competing.
const SecondaryButton = styled.button`
  width: 100%;
  flex: none;
  ${membershipSecondary}
  padding: 13px 0;
  min-height: 48px;
`;

const TextButton = styled.button`
  display: block;
  width: 100%;
  flex: none;
  background: none;
  border: none;
  color: #0f9bf0;
  font-size: 14px;
  font-weight: 600;
  padding: 14px 0;
  cursor: pointer;
  min-height: 44px;
`;

const MockHint = styled.div`
  margin-top: 20px;
  font-size: 11px;
  color: #8a94a6;
  font-family: monospace;
  text-align: center;
`;

interface StateProps {
  breakdownId: string | null;
  roleId: string | null;
  geoGuess: iRootState["submissionFlowModel"]["geoGuess"];
  submissionInfo: iRootState["submissionFlowModel"]["submissionInfo"];
  draft: iRootState["submissionSignupModel"]["draft"];
  accountDefaults: iRootState["submissionSignupModel"]["accountDefaults"];
  photo: iRootState["submissionSignupModel"]["photo"];
  resume: iRootState["submissionSignupModel"]["resume"];
  note: string;
  ageAttested: boolean;
  loading: boolean;
  errorCode: string | null;
  errorMessage: string | null;
  membership: iRootState["submissionStep4Model"]["membership"];
}

interface DispatchProps {
  loadMembership: () => Promise<any>;
  ensureDraft: (payload: { breakdownId: string | null; roleId: string | null }) => Promise<any>;
  uploadFile: (payload: { kind: "photo" | "resume"; file: File }) => Promise<any>;
  saveMaterials: () => Promise<any>;
  saveForLater: () => Promise<ISaveForLaterResponse | null>;
  setNote: (note: string) => void;
  setAgeAttested: (value: boolean) => void;
  refreshGate: (payload: { history: any; search?: string }) => Promise<any>;
  clearError: () => void;
  clearPhoto: () => void;
  pickPhoto: (photo: IMediaAssetDto) => void;
  pickResume: (resume: IMediaAssetDto | null) => void;
}

const mapStateToProps = (state: iRootState): StateProps => ({
  breakdownId: state.submissionFlowModel.breakdownId,
  roleId: state.submissionFlowModel.roleId,
  geoGuess: state.submissionFlowModel.geoGuess,
  submissionInfo: state.submissionFlowModel.submissionInfo,
  draft: state.submissionSignupModel.draft,
  accountDefaults: state.submissionSignupModel.accountDefaults,
  photo: state.submissionSignupModel.photo,
  resume: state.submissionSignupModel.resume,
  note: state.submissionSignupModel.note,
  ageAttested: state.submissionSignupModel.ageAttested,
  loading: state.submissionSignupModel.loading,
  errorCode: state.submissionSignupModel.errorCode,
  errorMessage: state.submissionSignupModel.errorMessage,
  membership: state.submissionStep4Model.membership,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  loadMembership: dispatch.submissionStep4Model.loadMembership,
  ensureDraft: dispatch.submissionSignupModel.ensureDraft,
  uploadFile: dispatch.submissionSignupModel.uploadFile,
  saveMaterials: dispatch.submissionSignupModel.saveMaterials,
  saveForLater: dispatch.submissionSignupModel.saveForLater,
  setNote: dispatch.submissionSignupModel.setNote,
  setAgeAttested: dispatch.submissionSignupModel.setAgeAttested,
  refreshGate: dispatch.submissionFlowModel.refreshGate,
  clearError: dispatch.submissionSignupModel.clearError,
  // setPhoto(null) is the reducer's own "no photo" state, so discarding needs
  // no new action.
  clearPhoto: () => dispatch.submissionSignupModel.setPhoto(null),
  // Picking from the library is the same state an upload ends in, minus the upload.
  pickPhoto: dispatch.submissionSignupModel.setPhoto,
  pickResume: dispatch.submissionSignupModel.setResume,
});

export default connect(mapStateToProps, mapDispatchToProps)(SubmitMaterialsPage);
